import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "edit-properties"
};
const _hoisted_2 = { class: "edit-properties__body" };
const _hoisted_3 = { class: "edit-properties__title" };
const _hoisted_4 = { class: "edit-properties__form-card" };
const _hoisted_5 = { class: "edit-properties__field" };
const _hoisted_6 = {
    for: "edit-properties-name",
    class: "edit-properties__label"
};
const _hoisted_7 = ["textContent"];
const _hoisted_8 = ["aria-invalid", "aria-describedby"];
const _hoisted_9 = { class: "edit-properties__field-row" };
const _hoisted_10 = { class: "edit-properties__field" };
const _hoisted_11 = ["textContent"];
const _hoisted_12 = { class: "edit-properties__field" };
const _hoisted_13 = {
    for: "edit-properties-source-date",
    class: "edit-properties__label"
};
const _hoisted_14 = { class: "edit-properties__field-row" };
const _hoisted_15 = { class: "edit-properties__field" };
const _hoisted_16 = ["textContent"];
const _hoisted_17 = { class: "edit-properties__radio-group" };
const _hoisted_18 = ["id", "checked", "value"];
const _hoisted_19 = ["for", "textContent"];
const _hoisted_20 = ["id", "checked", "value"];
const _hoisted_21 = ["for", "textContent"];
const _hoisted_22 = {
    key: 0,
    class: "edit-properties__field"
};
const _hoisted_23 = ["textContent"];
const _hoisted_24 = { class: "edit-properties__footer" };
const _hoisted_25 = ["disabled"];
import { computed, inject, ref, watch } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { formatFileSize, formatDurationCompact } from "@/common/helpers/format";
import { useDateTimeFormat } from "@/common/composables/date-time-format";
import { AUDIO_MIME_PREFIX } from "../constants";
import { ContentMode } from "@/types/external-content";
// Components
import ContentFileSummary from "./ContentFileSummary.vue";
import DropdownMenu from "@/common/components/DropdownMenu.vue";
import RecCalendar from "@/common/components/RecCalendar.vue";
import RecDialog from "@/common/components/RecDialog.vue";
import SvgIcon from "@/common/components/SvgIcon.vue";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'EditPropertiesDialog',
    props: /*@__PURE__*/ _mergeModels({
        content: {},
        languages: {},
        existingNames: {},
        saving: { type: Boolean }
    }, {
        "modelValue": { type: Boolean, ...{ required: true } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["save", "cancel"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const i18n = inject("i18n");
        const { toISODate } = useDateTimeFormat();
        const visible = _useModel(__props, "modelValue");
        // Local form state (cloned from content when dialog opens)
        const displayName = ref("");
        const language = ref("");
        const sourceDate = ref();
        const contentMode = ref();
        const hostCount = ref();
        // Computed
        const fileTypeIcon = computed(() => {
            return props.content?.mimeType?.startsWith(AUDIO_MIME_PREFIX) ? "icon-audio-file" : "icon-video-file";
        });
        const fileDisplayName = computed(() => {
            if (!props.content)
                return "";
            const { fileName, originalFilename, displayName } = props.content;
            return fileName || originalFilename || displayName;
        });
        const fileSummary = computed(() => {
            if (!props.content)
                return "";
            const size = props.content.fileSizeBytes ? formatFileSize(props.content.fileSizeBytes) : "";
            const duration = props.content.durationMs ? formatDurationCompact(props.content.durationMs / 1000) : "";
            if (size && duration)
                return `${size} \u00B7 ${duration}`;
            return size || duration;
        });
        const languageDisplay = computed(() => {
            if (!language.value)
                return "";
            return props.languages.find((l) => l.code === language.value)?.name ?? language.value;
        });
        const hostCountLabel = computed(() => {
            return !hostCount.value ? i18n.t("Auto-detect") : String(hostCount.value);
        });
        // Validation
        const uniqueName = helpers.withMessage(i18n.t("Name must be unique."), (value) => {
            if (!value)
                return true;
            const lower = value.toLowerCase().trim();
            const currentOriginalName = props.content?.displayName.toLowerCase().trim();
            if (lower === currentOriginalName)
                return true;
            return !(props.existingNames ?? []).some((n) => n.toLowerCase().trim() === lower);
        });
        const requiredName = helpers.withMessage(i18n.t("Name is required"), (value) => !!value?.trim());
        const validationRules = {
            displayName: { requiredName, uniqueName }
        };
        const v$ = useVuelidate(validationRules, { displayName }, { $rewardEarly: true });
        const isFormValid = computed(() => !v$.value.$invalid);
        // Watchers - clone content data when dialog opens
        watch(visible, (val) => {
            if (val && props.content) {
                displayName.value = props.content.displayName;
                language.value = props.content.language;
                contentMode.value = props.content.contentMode;
                hostCount.value = props.content.hostCount;
                if (props.content.sourceDate) {
                    const [y, m, d] = props.content.sourceDate.split("-").map(Number);
                    sourceDate.value = new Date(y, m - 1, d);
                }
                else {
                    sourceDate.value = undefined;
                }
                v$.value.$reset();
            }
        });
        watch(displayName, () => {
            v$.value.displayName.$touch();
        });
        // Handlers
        const onCancel = () => {
            visible.value = false;
            emit("cancel");
        };
        const onSave = async () => {
            const isValid = await v$.value.$validate();
            if (!isValid || !props.content || props.saving)
                return;
            emit("save", props.content.externalContentId, {
                displayName: displayName.value.trim(),
                sourceDate: sourceDate.value ? toISODate(sourceDate.value) : "",
                contentMode: contentMode.value ?? props.content.contentMode ?? ContentMode.CONVERSATION,
                hostCount: hostCount.value,
                language: language.value || props.content.language
            });
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(RecDialog, {
                modelValue: visible.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((visible).value = $event)),
                size: "large",
                padding: 20
            }, {
                default: _withCtx(() => [
                    (_ctx.content)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                            _createElementVNode("div", _hoisted_2, [
                                _createElementVNode("h2", _hoisted_3, _toDisplayString(_ctx.$t("Edit Properties")), 1),
                                _createVNode(ContentFileSummary, {
                                    icon: fileTypeIcon.value,
                                    name: fileDisplayName.value,
                                    meta: fileSummary.value
                                }, null, 8, ["icon", "name", "meta"]),
                                _createElementVNode("div", _hoisted_4, [
                                    _createElementVNode("div", _hoisted_5, [
                                        _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t("Name")), 1),
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).displayName.$errors, (error) => {
                                            return (_openBlock(), _createElementBlock("span", {
                                                id: "edit-properties-name-error",
                                                key: error.$uid,
                                                class: "label label-danger",
                                                role: "alert",
                                                textContent: _toDisplayString(error.$message)
                                            }, null, 8, _hoisted_7));
                                        }), 128)),
                                        _withDirectives(_createElementVNode("input", {
                                            id: "edit-properties-name",
                                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((displayName).value = $event)),
                                            type: "text",
                                            "aria-invalid": _unref(v$).displayName.$error,
                                            "aria-describedby": _unref(v$).displayName.$error ? 'edit-properties-name-error' : undefined,
                                            class: "edit-properties__input",
                                            maxlength: "255"
                                        }, null, 8, _hoisted_8), [
                                            [_vModelText, displayName.value]
                                        ])
                                    ]),
                                    _createElementVNode("div", _hoisted_9, [
                                        _createElementVNode("div", _hoisted_10, [
                                            _createElementVNode("label", {
                                                for: "edit-properties-language",
                                                class: "edit-properties__label",
                                                textContent: _toDisplayString(_ctx.$t('Language'))
                                            }, null, 8, _hoisted_11),
                                            _createVNode(DropdownMenu, {
                                                id: "edit-properties-language",
                                                type: "select",
                                                disabled: ""
                                            }, {
                                                button: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(languageDisplay.value) + " ", 1),
                                                    _createVNode(SvgIcon, {
                                                        icon: "icon-arrow-down",
                                                        class: "rec-dropdown__chevron"
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _createElementVNode("div", _hoisted_12, [
                                            _createElementVNode("label", _hoisted_13, _toDisplayString(_ctx.$t("Source Date")), 1),
                                            _createVNode(RecCalendar, {
                                                id: "edit-properties-source-date",
                                                "model-value": sourceDate.value,
                                                class: "no-highlight",
                                                "date-format": "M d, yy",
                                                "icon-display": "input",
                                                disabled: "",
                                                "no-icon": ""
                                            }, null, 8, ["model-value"])
                                        ])
                                    ]),
                                    _createElementVNode("div", _hoisted_14, [
                                        _createElementVNode("div", _hoisted_15, [
                                            _createElementVNode("span", {
                                                class: "edit-properties__label",
                                                textContent: _toDisplayString(_ctx.$t('Speakers'))
                                            }, null, 8, _hoisted_16),
                                            _createElementVNode("div", _hoisted_17, [
                                                _createElementVNode("input", {
                                                    id: `session-format-${_unref(ContentMode).CONVERSATION}`,
                                                    checked: _ctx.content.contentMode === _unref(ContentMode).CONVERSATION,
                                                    value: _unref(ContentMode).CONVERSATION,
                                                    type: "radio",
                                                    name: "session-format",
                                                    disabled: ""
                                                }, null, 8, _hoisted_18),
                                                _createElementVNode("label", {
                                                    for: `session-format-${_unref(ContentMode).CONVERSATION}`,
                                                    class: "wizard__radio-option",
                                                    textContent: _toDisplayString(_ctx.$t('Multiple'))
                                                }, null, 8, _hoisted_19),
                                                _createElementVNode("input", {
                                                    id: `session-format-${_unref(ContentMode).SINGLE_SPEAKER}`,
                                                    checked: _ctx.content.contentMode === _unref(ContentMode).SINGLE_SPEAKER,
                                                    value: _unref(ContentMode).SINGLE_SPEAKER,
                                                    type: "radio",
                                                    name: "session-format",
                                                    disabled: ""
                                                }, null, 8, _hoisted_20),
                                                _createElementVNode("label", {
                                                    for: `session-format-${_unref(ContentMode).SINGLE_SPEAKER}`,
                                                    class: "wizard__radio-option",
                                                    textContent: _toDisplayString(_ctx.$t('Single (no host)'))
                                                }, null, 8, _hoisted_21)
                                            ])
                                        ]),
                                        (contentMode.value === _unref(ContentMode).CONVERSATION)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                                _createElementVNode("label", {
                                                    for: "edit-properties-host-count",
                                                    class: "edit-properties__label",
                                                    textContent: _toDisplayString(_ctx.$t('Number of Hosts'))
                                                }, null, 8, _hoisted_23),
                                                _createVNode(DropdownMenu, {
                                                    id: "edit-properties-host-count",
                                                    type: "select",
                                                    disabled: "",
                                                    "menu-max-height": 210
                                                }, {
                                                    button: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(hostCountLabel.value) + " ", 1),
                                                        _createVNode(SvgIcon, {
                                                            icon: "icon-arrow-down",
                                                            class: "rec-dropdown__chevron"
                                                        })
                                                    ]),
                                                    _: 1
                                                })
                                            ]))
                                            : _createCommentVNode("", true)
                                    ])
                                ])
                            ]),
                            _createElementVNode("div", _hoisted_24, [
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "btn",
                                    onClick: onCancel
                                }, _toDisplayString(_ctx.$t("Cancel")), 1),
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "btn btn-primary",
                                    disabled: !isFormValid.value || _ctx.saving,
                                    onClick: onSave
                                }, _toDisplayString(_ctx.$t("Save")), 9, _hoisted_25)
                            ])
                        ]))
                        : _createCommentVNode("", true)
                ]),
                _: 1
            }, 8, ["modelValue"]));
        };
    }
});
